/**
 * Offline Queue Service
 * Manages write operations that should be synced when reconnected
 * Persists to IndexedDB, handles retry logic, and publishes sync events
 */

import type { OfflineQueueItem, OfflineQueueState } from '@/types'
import { pbClient } from '@/services/pbClient'
import { runtimeEnv } from '@/services/runtimeEnv'

const DB_NAME = 'clarix_offline'
const STORE_NAME = 'queue_items'
const STATE_STORE_NAME = 'queue_state'

type QueueOperation = 'CREATE' | 'UPDATE' | 'DELETE'

interface QueueItemInput {
  operation: QueueOperation
  collection: string
  recordId?: string
  data: Record<string, unknown>
}

function createInitialState(): OfflineQueueState {
  return {
    items: [],
    isSyncing: false,
    lastSyncTime: null,
    syncErrorCount: 0,
  }
}

class OfflineQueueService {
  private db: IDBDatabase | null = null
  private state: OfflineQueueState = createInitialState()
  private listeners: Set<(state: OfflineQueueState) => void> = new Set()
  private pendingFlush: Promise<void> | null = null
  private initializePromise: Promise<void> | null = null
  private isInitialized = false
  private syncListenersAttached = false
  private readonly onOnline = () => {
    console.info('[OfflineQueue] Reconnected, flushing queue')
    void this.flush()
  }

  private readonly onOffline = () => {
    console.warn('[OfflineQueue] Disconnected, operations will be queued')
  }

  /**
   * Initialize IndexedDB and restore queue from storage
   */
  async initialize(): Promise<void> {
    const config = runtimeEnv.getEnvironment()
    const isTestMode = import.meta.env.MODE === 'test'

    if (this.initializePromise) {
      return this.initializePromise
    }

    if (this.isInitialized && !isTestMode && typeof indexedDB !== 'undefined') {
      return
    }

    this.db = null
    this.state = createInitialState()
    this.pendingFlush = null

    if (isTestMode || typeof indexedDB === 'undefined') {
      this.isInitialized = true
      if (config.logLevel === 'debug') {
        console.debug('[OfflineQueue] IndexedDB unavailable, using in-memory state only')
      }

      return
    }

    const initPromise = new Promise<void>((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, 1)

      request.onerror = () => reject(request.error)
      request.onsuccess = async () => {
        this.db = request.result

        // Restore persisted state
        await this.restoreState()

        // Setup sync listeners
        this.setupSyncListeners()
        this.isInitialized = true

        if (config.logLevel === 'debug') {
          console.debug('[OfflineQueue] Initialized with', this.state.items.length, 'items')
        }

        resolve()
      }

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'id' })
        }
        if (!db.objectStoreNames.contains(STATE_STORE_NAME)) {
          db.createObjectStore(STATE_STORE_NAME)
        }
      }
    }).finally(() => {
      this.initializePromise = null
    })

    this.initializePromise = initPromise
    return initPromise
  }

  /**
   * Add operation to queue
   */
  async enqueue(input: QueueItemInput): Promise<void> {
    if (!runtimeEnv.getEnvironment().features.offlineQueue) {
      // If offline queue disabled, try to sync immediately
      await this.syncSingleOperation(input)
      return
    }

    const item: OfflineQueueItem = {
      id: this.generateId(),
      operation: input.operation,
      collection: input.collection,
      recordId: input.recordId || this.generateId(),
      data: input.data,
      timestamp: Date.now(),
      retries: 0,
      lastError: undefined,
    }

    this.state.items.push(item)
    await this.persistQueue()
    this.notifyListeners()

    console.info('[OfflineQueue] Enqueued:', item.id, item.operation, item.collection)

    // Try to sync if online
    if (navigator.onLine && import.meta.env.MODE !== 'test') {
      this.flush()
    }
  }

  /**
   * Get current queue state
   */
  getState(): OfflineQueueState {
    return { ...this.state }
  }

  /**
   * Subscribe to queue state changes
   */
  subscribe(listener: (state: OfflineQueueState) => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  /**
   * Flush queue (sync all pending items)
   */
  async flush(): Promise<void> {
    if (this.pendingFlush) {
      return this.pendingFlush
    }

    this.pendingFlush = this.doFlush().finally(() => {
      this.pendingFlush = null
    })

    return this.pendingFlush
  }

  /**
   * Clear queue (dangerous, use with caution)
   */
  async clear(): Promise<void> {
    if (!this.db) {
      this.state.items = []
      this.notifyListeners()
      return
    }

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction([STORE_NAME], 'readwrite')
      const store = tx.objectStore(STORE_NAME)
      const request = store.clear()

      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        this.state.items = []
        this.notifyListeners()
        resolve()
      }
    })
  }

  // --- Private methods ---

  private async doFlush(): Promise<void> {
    if (this.state.isSyncing || this.state.items.length === 0) {
      return
    }

    this.state.isSyncing = true
    this.notifyListeners()

    const itemsToSync = [...this.state.items]
    const failed: OfflineQueueItem[] = []

    for (const item of itemsToSync) {
      const success = await this.syncItem(item)
      if (!success) {
        failed.push(item)
      } else {
        this.state.items = this.state.items.filter((i) => i.id !== item.id)
      }
    }

    if (failed.length === 0) {
      this.state.lastSyncTime = Date.now()
      this.state.syncErrorCount = 0
    } else {
      this.state.syncErrorCount++
    }

    this.state.isSyncing = false
    await this.persistQueue()
    this.notifyListeners()

    console.info('[OfflineQueue] Sync completed:', itemsToSync.length - failed.length, 'succeed,', failed.length, 'failed')
  }

  private async syncItem(item: OfflineQueueItem): Promise<boolean> {
    try {
      await this.syncSingleOperation({
        operation: item.operation,
        collection: item.collection,
        recordId: item.recordId,
        data: item.data,
      })
      return true
    } catch (err) {
      item.lastError = err instanceof Error ? err.message : String(err)
      item.retries++
      return false
    }
  }

  private async syncSingleOperation(input: QueueItemInput): Promise<void> {
    if (!pbClient.isAuthenticated()) {
      throw new Error('Not authenticated')
    }

    switch (input.operation) {
      case 'CREATE':
        await pbClient.createRecord(input.collection, input.data)
        break
      case 'UPDATE':
        if (!input.recordId) throw new Error('recordId required for UPDATE')
        await pbClient.updateRecord(input.collection, input.recordId, input.data)
        break
      case 'DELETE':
        if (!input.recordId) throw new Error('recordId required for DELETE')
        await pbClient.deleteRecord(input.collection, input.recordId)
        break
    }
  }

  private async persistQueue(): Promise<void> {
    if (!this.db) return

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction([STORE_NAME, STATE_STORE_NAME], 'readwrite')

      // Clear and re-insert all items
      const itemStore = tx.objectStore(STORE_NAME)
      itemStore.clear().onsuccess = () => {
        this.state.items.forEach((item) => itemStore.put(item))
      }

      // Persist state
      const stateStore = tx.objectStore(STATE_STORE_NAME)
      stateStore.put(this.state, 'current')

      tx.onerror = () => reject(tx.error)
      tx.oncomplete = () => resolve()
    })
  }

  private async restoreState(): Promise<void> {
    if (!this.db) return

    return new Promise((resolve) => {
      const tx = this.db!.transaction([STORE_NAME, STATE_STORE_NAME], 'readonly')

      // Restore items
      const itemStore = tx.objectStore(STORE_NAME)
      itemStore.getAll().onsuccess = (event) => {
        this.state.items = (event.target as IDBRequest).result || []
      }

      // Restore state
      const stateStore = tx.objectStore(STATE_STORE_NAME)
      stateStore.get('current').onsuccess = (event) => {
        const stored = (event.target as IDBRequest).result
        if (stored) {
          this.state = { ...this.state, ...stored }
        }
      }

      tx.oncomplete = () => resolve()
    })
  }

  private setupSyncListeners(): void {
    if (this.syncListenersAttached || typeof window === 'undefined') return

    window.addEventListener('online', this.onOnline)
    window.addEventListener('offline', this.onOffline)
    this.syncListenersAttached = true
  }

  private notifyListeners(): void {
    this.listeners.forEach((listener) => listener(this.getState()))
  }

  private generateId(): string {
    return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }
}

// Singleton instance
export const offlineQueue = new OfflineQueueService()

export default offlineQueue
